"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Disc, Search as SearchIcon, LayoutGrid, List, SlidersHorizontal } from "lucide-react";
import { useTranslations } from "next-intl";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { useState, useEffect, useMemo } from "react";
import { getAllReleases } from "@/services/admin";
import { releaseIsPlayable } from "@/services/home";
import { cn } from "@/lib/utils";
import { useScrollDrag } from "@/hooks/use-scroll-drag";
import { useRef } from "react";
import { TiltCard, CoverExplicitSticker } from "@/components/media/tilt-card";
import { useUrlParams } from "@/hooks/use-url-params";

// ─── Types ─────────────────────────────────────────────────────────────────────

type ViewMode = "grid" | "list";
type SortMode = "az" | "za" | "newest" | "oldest";

const RELEASE_TYPES = ["Album", "EP", "Single", "Mixtape", "Compilation", "Live Album", "Demo Compilation"];

const sortLabel: Record<SortMode, string> = {
  az: "A – Z",
  za: "Z – A",
  newest: "Newest",
  oldest: "Oldest",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getArtistName(rel: any): string {
  if (!rel.artist) return "Unknown Artist";
  if (typeof rel.artist === "string") return rel.artist;
  return rel.artist?.name || rel.artistName || "Unknown Artist";
}

function getReleaseDate(rel: any): number {
  const d = rel.releaseDate || rel.createdAt;
  if (!d) return 0;
  try { return new Date(d).getTime(); } catch { return 0; }
}

function sortReleases(releases: any[], sort: SortMode): any[] {
  return [...releases].sort((a, b) => {
    if (sort === "az") return (a.title || "").localeCompare(b.title || "");
    if (sort === "za") return (b.title || "").localeCompare(a.title || "");
    if (sort === "newest") return getReleaseDate(b) - getReleaseDate(a);
    return getReleaseDate(a) - getReleaseDate(b);
  });
}

const typeColorMap: Record<string, string> = {
  album: "bg-primary/20 text-primary",
  ep: "bg-purple-500/20 text-purple-400",
  single: "bg-blue-500/20 text-blue-400",
  mixtape: "bg-orange-500/20 text-orange-400",
  compilation: "bg-green-500/20 text-green-400",
  "live album": "bg-red-500/20 text-red-400",
};

// ─── Release Card ────────────────────────────────────────────────────────────

function ReleaseCard({ rel, viewMode }: { rel: any; viewMode: ViewMode }) {
  const artistName = getArtistName(rel);
  const type = (rel.releaseType || "release").toLowerCase();
  const typeBadgeClass = typeColorMap[type] || "bg-muted text-muted-foreground";
  const trackCount = (rel.tracks || []).length;
  const href = `/music/${rel.id}`;
  // Resolve thumbnail from multiple possible fields
  const thumb = rel.coverArtUrl || rel.imageUrl || (Array.isArray(rel.images) && rel.images[0]) || '';

  if (viewMode === "list") {
    return (
      <Link href={href} className="group flex items-center gap-4 p-3 rounded-lg hover:bg-secondary/40 transition-colors border border-border/30 hover:border-border/60">
        <div className="relative w-14 h-14 rounded-md overflow-hidden bg-muted flex-shrink-0">
          {thumb ? (
            <Image src={thumb} alt={rel.title || ""} fill className="object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Disc className="h-6 w-6 text-muted-foreground/40" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="capitalize font-semibold truncate text-sm group-hover:text-primary transition-colors">{rel.title || "Untitled"}</p>
          <p className="capitalize text-xs text-muted-foreground truncate">{artistName}</p>
          {trackCount > 0 && <p className="text-xs text-muted-foreground">{trackCount} track{trackCount !== 1 ? "s" : ""}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge className={cn("text-[10px] px-2 py-0 h-5 capitalize font-medium rounded-full", typeBadgeClass)}>
            {rel.releaseType || "Release"}
          </Badge>
          {rel.releaseDate && (
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {new Date(rel.releaseDate).getFullYear()}
            </span>
          )}
        </div>
      </Link>
    );
  }

  return (
    <TiltCard>
    <Link href={href} className="group block rounded-lg overflow-hidden bg-card border border-border/30 hover:border-primary/40 hover:shadow-lg transition-all">
      <div className="relative aspect-square bg-muted">
        {thumb ? (
          <Image src={thumb} alt={rel.title || ""} fill className="object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Disc className="h-10 w-10 text-muted-foreground/30" />
          </div>
        )}
        {/* Type badge overlay */}
        <div className="absolute top-2 left-2">
          <Badge className={cn("text-[10px] px-2 py-0 h-5 capitalize font-medium rounded-full backdrop-blur-sm", typeBadgeClass)}>
            {rel.releaseType || "Release"}
          </Badge>
        </div>
        {trackCount > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full backdrop-blur-sm">
            {trackCount} {trackCount === 1 ? "track" : "tracks"}
          </div>
        )}
        <CoverExplicitSticker explicit={rel.explicit} />
      </div>
      <div className="p-3 space-y-0.5">
        <p className="capitalize font-semibold text-sm truncate group-hover:text-primary transition-colors">{rel.title || "Untitled"}</p>
        <p className="capitalize text-xs text-muted-foreground truncate">{artistName}</p>
        {rel.releaseDate && (
          <p className="text-[10px] text-muted-foreground/60">
            {new Date(rel.releaseDate).getFullYear()}
          </p>
        )}
      </div>
    </Link>
    </TiltCard>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReleasesPage() {
  const [releases, setReleases] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeType, setActiveType] = useState<string>("all");
  const [activeGenre, setActiveGenre] = useState<string>("all");
  // ?genre=Jazz — coπ's "show me jazz" opens the releases in that style (Gordon 2026-11-02).
  useUrlParams((sp) => { const g = sp.get("genre"); if (g) setActiveGenre(g); });
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortMode, setSortMode] = useState<SortMode>("newest");
  const tNav = useTranslations("nav");
  const tCommon = useTranslations("common");
  const tBrowse = useTranslations("browse");
  // Filter buttons fold away behind a toggle beside the search (Gordon
  // 2026-10-31: "on mobile too many filter buttons visible as default").
  // Closed on phones, open from sm up; an explicit choice is remembered.
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    let saved: string | null = null;
    try { saved = localStorage.getItem("bb-releases-filters"); } catch { /* ignore */ }
    setFiltersOpen(saved ? saved === "open" : window.matchMedia("(min-width: 640px)").matches);
  }, []);
  const toggleFilters = () => setFiltersOpen(prev => {
    try { localStorage.setItem("bb-releases-filters", prev ? "closed" : "open"); } catch { /* ignore */ }
    return !prev;
  });

  const typeScrollRef = useRef<HTMLDivElement>(null);
  const genreScrollRef = useRef<HTMLDivElement>(null);
  useScrollDrag(typeScrollRef);
  useScrollDrag(genreScrollRef);

  useEffect(() => {
    getAllReleases()
      // Same visibility rule as the home page (services/home.ts): admin-hidden,
      // hidden, and audio-less metadata imports (AWAL CSV etc.) never show on
      // the public browse page.
      .then((data) => setReleases(data.filter((r: any) => !r.adminHidden && r.hidden !== true && releaseIsPlayable(r))))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  // Unique genres across all releases
  const allGenres = useMemo(() => {
    const set = new Set<string>();
    releases.forEach((r) => {
      (r.genres || (r.genre ? [r.genre] : [])).forEach((g: string) => g && set.add(g));
    });
    return Array.from(set).sort();
  }, [releases]);

  // Unique release types present in the data
  const presentTypes = useMemo(() => {
    const set = new Set<string>();
    releases.forEach((r) => {
      const t = r.releaseType?.toLowerCase();
      if (t) set.add(t);
    });
    return RELEASE_TYPES.filter((t) => set.has(t.toLowerCase()));
  }, [releases]);

  const filtered = useMemo(() => {
    let list = releases;
    if (activeType !== "all") {
      list = list.filter((r) => (r.releaseType || "").toLowerCase() === activeType.toLowerCase());
    }
    if (activeGenre !== "all") {
      list = list.filter((r) => {
        const genres: string[] = r.genres || (r.genre ? [r.genre] : []);
        return genres.some((g) => g.toLowerCase() === activeGenre.toLowerCase());
      });
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (r) =>
          (r.title || "").toLowerCase().includes(term) ||
          getArtistName(r).toLowerCase().includes(term) ||
          (r.label || "").toLowerCase().includes(term)
      );
    }
    return sortReleases(list, sortMode);
  }, [releases, activeType, activeGenre, searchTerm, sortMode]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Disc className="h-12 w-12 text-muted-foreground animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Loading releases…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Sticky header ── */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-[50px] z-30">
        <div className="w-full px-page pt-3 pb-2 space-y-2">
          <div className="flex items-center gap-2">
            <Breadcrumbs heading size="lg" items={[{ label: tNav("releases"), icon: Disc }]} />
            <span className="text-xs text-muted-foreground shrink-0">
              {filtered.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="search"
                placeholder={tBrowse("search", { title: tNav("releases") })}
                aria-label={tBrowse("search", { title: tNav("releases") })}
                className="pl-9 h-9 bg-secondary/50"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter toggle — a dot marks an active filter while folded away */}
            <Button
              variant={filtersOpen ? "default" : "outline"}
              size="icon"
              className="relative h-9 w-9 shrink-0"
              onClick={toggleFilters}
              aria-pressed={filtersOpen}
              aria-expanded={filtersOpen}
              aria-controls="releases-filters"
              aria-label={tCommon("filter")}
              title={tCommon("filter")}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {!filtersOpen && (activeType !== "all" || activeGenre !== "all" || sortMode !== "newest") && (
                <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
              )}
            </Button>

              {/* View toggle */}
              <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setViewMode("grid")}
                  title="Grid view"
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setViewMode("list")}
                  title="List view"
                >
                  <List className="h-3.5 w-3.5" />
                </Button>
              </div>
          </div>
        </div>

        {filtersOpen && (
        <div id="releases-filters">
        <div className="px-page pb-2">
              {/* Sort */}
              <div className="flex items-center gap-1">
                {(Object.keys(sortLabel) as SortMode[]).map((s) => (
                  <Button
                    key={s}
                    variant={sortMode === s ? "default" : "outline"}
                    size="sm"
                    className="h-8 px-3 text-xs"
                    onClick={() => setSortMode(s)}
                  >
                    {sortLabel[s]}
                  </Button>
                ))}
              </div>
        </div>

        {/* Type filter chips */}
        {presentTypes.length > 0 && (
          <div className="px-page pb-2">
            <div ref={typeScrollRef} className="flex gap-2 overflow-x-auto hide-scrollbar drag-scroll sm:cursor-auto">
              <Button
                key="all"
                variant={activeType === "all" ? "default" : "outline"}
                size="sm"
                className="h-7 px-3 text-xs flex-shrink-0 rounded-full"
                onClick={() => setActiveType("all")}
              >
                All types
              </Button>
              {presentTypes.map((t) => (
                <Button
                  key={t}
                  variant={activeType === t.toLowerCase() ? "default" : "outline"}
                  size="sm"
                  className="h-7 px-3 text-xs flex-shrink-0 rounded-full capitalize"
                  onClick={() => setActiveType(activeType === t.toLowerCase() ? "all" : t.toLowerCase())}
                >
                  {t}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Genre filter chips */}
        {allGenres.length > 0 && (
          <div className="px-page pb-3">
            <div ref={genreScrollRef} className="flex gap-2 overflow-x-auto hide-scrollbar drag-scroll sm:cursor-auto sm:flex-wrap">
              <Button
                key="all-genre"
                variant={activeGenre === "all" ? "default" : "outline"}
                size="sm"
                className="h-7 px-3 text-xs flex-shrink-0 rounded-full"
                onClick={() => setActiveGenre("all")}
              >
                All genres
              </Button>
              {allGenres.map((g) => (
                <Button
                  key={g}
                  variant={activeGenre === g ? "default" : "outline"}
                  size="sm"
                  className="h-7 px-3 text-xs flex-shrink-0 rounded-full capitalize"
                  onClick={() => setActiveGenre(activeGenre === g ? "all" : g)}
                >
                  {g}
                </Button>
              ))}
            </div>
          </div>
        )}
        </div>
        )}
      </div>

      {/* ── Content ── */}
      <div className="px-page py-6">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Disc className="h-12 w-12 text-muted-foreground/30" />
            <p className="text-muted-foreground text-sm">
              {releases.length === 0 ? "No releases yet." : "No releases match your filters."}
            </p>
            {(activeType !== "all" || activeGenre !== "all" || searchTerm) && (
              <Button variant="outline" size="sm" onClick={() => { setActiveType("all"); setActiveGenre("all"); setSearchTerm(""); }}>
                Clear filters
              </Button>
            )}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filtered.map((rel) => (
              <ReleaseCard key={rel.id} rel={rel} viewMode="grid" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((rel) => (
              <ReleaseCard key={rel.id} rel={rel} viewMode="list" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
